/* ═══════════════════════════════════════════════════════════════════════════
   messwithfilm — le carrousel du héros.

   La première version n'avait pas de JavaScript : les diapos partageaient un
   @keyframes décalé d'une durée à chaque fois. C'était élégant et muet — on ne
   pouvait pas aller à une diapo précise, et les textes ne pouvaient pas
   changer avec l'image, puisque rien ne savait quelle diapo était à l'écran.

   Ici, une seule chose bouge : la classe « est-actif ». Le fondu, le
   remplissage des traits, tout le reste est en CSS. Si ce fichier ne se charge
   pas, la première diapo reste affichée — elle porte la classe dès le rendu du
   serveur — et la page ne casse pas.
   ═══════════════════════════════════════════════════════════════════════════ */

class MwfCarrousel extends HTMLElement {
  connectedCallback() {
    this.images = Array.from(this.querySelectorAll('.mwf-heros__image'));
    this.pages = Array.from(this.querySelectorAll('.mwf-heros__page'));
    this.puces = Array.from(this.querySelectorAll('.mwf-heros__puce'));
    this.n = Math.max(this.images.length, this.pages.length);
    if (this.n < 2) return;

    this.duree = parseInt(this.dataset.duree, 10) || 6000;
    this.i = 0;
    this.calme = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.puces.forEach((bouton, k) => {
      bouton.addEventListener('click', () => this.va(k));
    });

    // Flèches du clavier, dès que le focus est quelque part dans le carrousel.
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.va(this.i + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.va(this.i - 1); }
    });

    // Glissement au doigt. Le seuil de 40 px écarte les faux positifs d'un
    // défilement vertical, et les écouteurs sont passifs : on ne bloque jamais
    // le défilement de la page.
    let depart = null;
    let departY = 0;
    this.addEventListener('touchstart', (e) => {
      depart = e.touches[0].clientX;
      departY = e.touches[0].clientY;
    }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (depart === null) return;
      const dx = e.changedTouches[0].clientX - depart;
      const dy = e.changedTouches[0].clientY - departY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        this.va(this.i + (dx < 0 ? 1 : -1));
      }
      depart = null;
    }, { passive: true });

    // La lecture s'arrête pendant qu'on regarde de près — souris posée dessus
    // ou focus au clavier — et quand l'onglet passe en arrière-plan, sinon le
    // minuteur continue de tourner dans le vide.
    this.addEventListener('pointerenter', () => this.arrete());
    this.addEventListener('pointerleave', () => this.lance());
    this.addEventListener('focusin', () => this.arrete());
    this.addEventListener('focusout', () => {
      if (!this.matches(':hover')) this.lance();
    });
    this._visibilite = () => (document.hidden ? this.arrete() : this.lance());
    document.addEventListener('visibilitychange', this._visibilite);

    // Qui a demandé moins d'animation garde la main : les traits restent
    // cliquables, rien ne défile tout seul.
    this.calme.addEventListener('change', () => (this.calme.matches ? this.arrete() : this.lance()));

    // Les diapos suivantes sont en `lazy` dans le HTML, pour ne pas disputer la
    // bande passante à la première — celle que mesure le LCP. Mais une diapo
    // atteinte au clic avant l'arrivée de son image s'afficherait vide : on les
    // fait donc charger dès que le navigateur n'a plus rien d'urgent à faire.
    const precharge = () => this.images.forEach((i) => { if (i.loading === 'lazy') i.loading = 'eager'; });
    if ('requestIdleCallback' in window) requestIdleCallback(precharge, { timeout: 3000 });
    else setTimeout(precharge, 1500);

    this.montre(0);
    this.lance();
  }

  disconnectedCallback() {
    this.arrete();
    document.removeEventListener('visibilitychange', this._visibilite);
  }

  /* Affiche la diapo k et remet le minuteur à zéro : un clic donne droit à une
     durée pleine, sinon la diapo choisie disparaîtrait au bout du reliquat. */
  va(k) {
    this.montre(((k % this.n) + this.n) % this.n);
    if (this.minuteur) this.lance();
  }

  montre(k) {
    this.i = k;
    const marque = (liste) => liste.forEach((e, j) => e.classList.toggle('est-actif', j === k));
    marque(this.images);
    marque(this.pages);
    marque(this.puces);
    this.puces.forEach((b, j) => b.setAttribute('aria-current', j === k ? 'true' : 'false'));
    this.remplit(this.minuteur != null);
  }

  /* Le trait de la diapo courante se remplit sur sa durée d'affichage : c'est
     ce qui dit que ça avance, et de combien. Arrêté, il reste plein.
     Les autres sont remis à vide — sans quoi ils gardaient le remplissage de
     leur passage précédent, et trois traits pleins sur quatre ne désignaient
     plus rien. */
  remplit(anime) {
    this.puces.forEach((bouton, j) => {
      const barre = bouton.firstElementChild;
      if (!barre || j === this.i) return;
      barre.style.transition = 'none';
      barre.style.transform = 'scaleX(0)';
    });
    const barre = this.puces[this.i] && this.puces[this.i].firstElementChild;
    if (!barre) return;
    barre.style.transition = 'none';
    barre.style.transform = 'scaleX(0)';
    void barre.offsetWidth; // force le recalcul, sinon les deux états fusionnent
    if (anime && !this.calme.matches) {
      barre.style.transition = 'transform ' + this.duree + 'ms linear';
    }
    barre.style.transform = 'scaleX(1)';
  }

  lance() {
    if (this.calme.matches || document.hidden) return;
    clearInterval(this.minuteur);
    this.minuteur = setInterval(() => this.montre((this.i + 1) % this.n), this.duree);
    this.remplit(true);
  }

  arrete() {
    clearInterval(this.minuteur);
    this.minuteur = null;
    // Figer le trait là où il en est plutôt que le laisser filer jusqu'au bout.
    const barre = this.puces[this.i] && this.puces[this.i].firstElementChild;
    if (barre) {
      const etat = getComputedStyle(barre).transform;
      barre.style.transition = 'none';
      barre.style.transform = etat;
    }
  }
}

if (!customElements.get('mwf-carrousel')) {
  customElements.define('mwf-carrousel', MwfCarrousel);
}
